import { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../flux/stores/hooks';
import { deleteModelGroup } from '../../../../flux/actions/apsThunks';
import { useApiAccessToken } from '../../../../utils/useApiAccessToken';
import { outgoingLinks } from '../../../../flux/utils/rvtGraph';
import { DisciplineChips } from '../DisciplineChips/DisciplineChips';
import { ModelGroupEditor } from '../ModelGroupEditor/ModelGroupEditor';
import './ModelGroupCard.css';

/** One model group: its view row, or — while being edited — the editor in its place. */
export const ModelGroupCard = ({
    projectId,
    group,
    allGroups,
    nodesByUrn,
    edges,
    isEditing,
    onStartEdit,
    onDone,
}) => {
    const dispatch = useAppDispatch();
    const { getAccessToken } = useApiAccessToken();
    const mutation = useAppSelector(state => state.projects.aps[projectId]?.modelGroups.mutation)
        ?? { isSaving: false, error: null };
    const [confirmingDelete, setConfirmingDelete] = useState(false);

    const handleDelete = async () => {
        let accessToken;
        try {
            accessToken = await getAccessToken();
        } catch (error) {
            console.error('Failed to acquire API token to delete the model group:', error);
            return;
        }
        if (!accessToken) return;
        const result = await dispatch(deleteModelGroup({ accessToken, projectId, groupId: group.object_id }));
        if (deleteModelGroup.fulfilled.match(result)) setConfirmingDelete(false);
    };

    if (isEditing) {
        return (
            <div className="mg-card mg-card-editing">
                <ModelGroupEditor
                    projectId={projectId}
                    group={group}
                    allGroups={allGroups}
                    nodesByUrn={nodesByUrn}
                    edges={edges}
                    onDone={onDone}
                    onCancel={onDone}
                />
            </div>
        );
    }

    const memberUrns = new Set(group.members.map(m => m.lineage_urn));

    return (
        <div className="mg-card">
            <div className="mg-card-header">
                <h4 className="mg-card-title">{group.name}</h4>
                <span className="mg-card-count">
                    {group.members.length} model{group.members.length === 1 ? '' : 's'} selected
                </span>
                <div className="mg-card-actions">
                    <button type="button" className="rules-btn-ghost" onClick={onStartEdit}>Edit</button>
                    <button type="button" className="rules-btn-ghost pd-danger" onClick={() => setConfirmingDelete(true)}>
                        Delete
                    </button>
                </div>
            </div>

            <p className="mg-card-subtitle">
                Models picked from this project's connected ACC folder, scanned together as one set.
            </p>

            {group.members.map(member => {
                const linksOutside = outgoingLinks(member.lineage_urn, edges).filter(urn => !memberUrns.has(urn));

                return (
                    <div key={member.lineage_urn} className="mg-member-row mg-member-row-view">
                        <span className="mg-model-name">{member.name ?? member.lineage_urn}</span>
                        {member.missing_in_acc === true && (
                            <span className="mg-model-missing">Missing from ACC</span>
                        )}
                        <DisciplineChips value={member.disciplines} readOnly />
                        {linksOutside.length > 0 && (
                            <span className="mg-model-links-note">
                                Links to {linksOutside.length} model{linksOutside.length === 1 ? '' : 's'} not in this group.
                            </span>
                        )}
                    </div>
                );
            })}

            {confirmingDelete && (
                <div className="members-modal-backdrop" role="presentation">
                    <div className="members-modal" role="dialog" aria-modal="true" aria-labelledby="delete-group-title">
                        <h2 id="delete-group-title">Delete "{group.name}"?</h2>
                        <p>
                            This removes the group. A model that isn't in another group is dropped from the
                            project, and its disciplines will need to be re-entered if it's added again.
                        </p>
                        {mutation.error && <p className="org-error">{mutation.error.message}</p>}
                        <div className="pw-actions">
                            <button
                                type="button"
                                className="members-remove-button"
                                onClick={handleDelete}
                                disabled={mutation.isSaving}
                            >
                                {mutation.isSaving ? 'Deleting…' : 'Delete'}
                            </button>
                            <button
                                type="button"
                                className="rules-btn-ghost"
                                onClick={() => setConfirmingDelete(false)}
                                disabled={mutation.isSaving}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
